import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Crown, PlayCircle, FileDown, Activity, ArrowRight, History } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [videos, setVideos] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    api.get("/health-profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/videos").then(({ data }) => setVideos(data)).catch(() => {});
    api.get("/pdfs").then(({ data }) => setPdfs(data)).catch(() => {});
    api.get("/downloads/me").then(({ data }) => setDownloads(data)).catch(() => {});
  }, []);

  const isPaid = user?.subscription_status === "paid" || user?.role === "admin";
  const isPending = user?.subscription_status === "pending";

  return (
    <div className="container-page py-10" data-testid="dashboard-page">
      {/* Greeting + status */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink">Hi, {user?.full_name?.split(" ")[0] || "friend"}!</h1>
          <p className="mt-1 text-muted2">Welcome to your wellness dashboard.</p>
        </div>
        <div data-testid="subscription-status-banner"
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border ${isPaid ? "bg-leaf/10 text-leaf border-leaf/30" : isPending ? "bg-warm/10 text-warm border-warm/30" : "bg-cardm text-muted2 border-subtle"}`}>
          <Crown size={16} />
          {isPaid ? "Subscribed — full access" : isPending ? "Subscription pending review" : "Free account"}
        </div>
      </div>

      {/* Stat cards */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white border border-subtle rounded-2xl p-6 shadow-ambient" data-testid="profile-summary-card">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-moss/10 text-moss grid place-items-center"><Activity size={18} /></span>
            <h3 className="font-heading font-semibold text-ink">Health profile</h3>
          </div>
          {profile ? (
            <ul className="mt-4 space-y-1.5 text-sm text-muted2">
              <li><span className="text-soft">Age:</span> {profile.age} • <span className="text-soft">Gender:</span> {profile.gender}</li>
              <li><span className="text-soft">BMI:</span> <strong className="text-ink">{profile.bmi}</strong></li>
              <li><span className="text-soft">Goal:</span> {profile.goal}</li>
              <li><span className="text-soft">Activity:</span> {profile.activity_level}</li>
            </ul>
          ) : (
            <p className="mt-4 text-sm text-muted2">No profile yet.</p>
          )}
          <Link to="/health-profile" className="mt-4 inline-flex items-center gap-1 text-sm text-moss font-medium hover:underline" data-testid="edit-profile-link">
            {profile ? "Edit profile" : "Complete profile"} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="bg-white border border-subtle rounded-2xl p-6 shadow-ambient">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-terra/10 text-terra grid place-items-center"><PlayCircle size={18} /></span>
            <h3 className="font-heading font-semibold text-ink">Video library</h3>
          </div>
          <p className="mt-4 text-3xl font-heading font-bold text-ink">{videos.length}</p>
          <p className="text-sm text-muted2">videos available</p>
          <Link to="/videos" className="mt-3 inline-flex items-center gap-1 text-sm text-moss font-medium hover:underline">
            Open library <ArrowRight size={14} />
          </Link>
        </div>

        <div className="bg-white border border-subtle rounded-2xl p-6 shadow-ambient">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-leaf/10 text-leaf grid place-items-center"><FileDown size={18} /></span>
            <h3 className="font-heading font-semibold text-ink">PDF guides</h3>
          </div>
          <p className="mt-4 text-3xl font-heading font-bold text-ink">{pdfs.length}</p>
          <p className="text-sm text-muted2">guides ready to download</p>
          <Link to="/pdfs" className="mt-3 inline-flex items-center gap-1 text-sm text-moss font-medium hover:underline">
            Open library <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Subscribe CTA */}
      {!isPaid && (
        <div className="mt-8 rounded-3xl bg-moss text-white p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-center" data-testid="subscribe-cta">
          <div>
            <h2 className="font-heading text-2xl lg:text-3xl font-semibold leading-tight">
              {isPending
                ? "Your request is being reviewed"
                : user?.has_health_profile
                  ? "Activate your access"
                  : "Complete your health profile"}
            </h2>
            <p className="mt-2 text-white/80">
              {isPending
                ? "We'll activate your account as soon as your subscription is approved."
                : user?.has_health_profile
                  ? "Your profile is ready — choose an access option to unlock all videos and PDF guides."
                  : "Tell us about your goals and lifestyle so we can tailor your library. It only takes a couple of minutes."}
            </p>
          </div>
          <div className="lg:text-right">
            {!isPending && (
              <Link
                to={user?.has_health_profile ? "/pricing" : "/health-profile"}
                data-testid="dashboard-subscribe-button"
                className="inline-flex items-center gap-2 bg-terra hover:bg-terra-dark text-white rounded-full px-7 py-3.5 font-semibold transition-all active:scale-95"
              >
                {user?.has_health_profile ? "View access options" : "Complete profile"} <ArrowRight size={18} />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Download history */}
      <div className="mt-10">
        <h2 className="font-heading text-xl font-semibold text-ink flex items-center gap-2"><History size={20} className="text-moss" /> Recent downloads</h2>
        <div className="mt-4 bg-white border border-subtle rounded-2xl divide-y divide-subtle" data-testid="download-history">
          {downloads.length === 0 ? (
            <p className="p-6 text-sm text-muted2">No downloads yet.</p>
          ) : downloads.slice(0, 8).map((d) => (
            <div key={d.id} className="p-4 flex items-center justify-between text-sm">
              <span className="text-ink font-medium">{d.pdf_title}</span>
              <span className="text-soft">{new Date(d.downloaded_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
